import { map } from "ramda";

import { type BlastHit, type BlastHsp, type BlastResult, type Range } from "../graphql.js";
import { HIT_MISSING_ACCESSION } from "../settings/settings.js";

export function stripDatabasePrefix(databaseAccessionVersion: string) {
  const index = databaseAccessionVersion.indexOf(":");
  return index === -1 ? databaseAccessionVersion : databaseAccessionVersion.substr(index + 1);
}

export function formatScore(value: number): number {
  return Number.parseFloat(value.toFixed(4));
}

export function getHspQueryRange(hsp: BlastHsp): Range {
  if (hsp.queryStart <= hsp.queryStop) {
    return { start: hsp.queryStart, stop: hsp.queryStop };
  } else {
    return { start: hsp.queryStop, stop: hsp.queryStart };
  }
}

export function getHspHitRange(hsp: BlastHsp): Range {
  if (hsp.targetStart <= hsp.targetStop) {
    return { start: hsp.targetStart, stop: hsp.targetStop };
  } else {
    return { start: hsp.targetStop, stop: hsp.targetStart };
  }
}

export function getHitQueryRange(hit: BlastHit): Range {
  const start: number = Math.min(...map((hsp: BlastHsp) => hsp.queryStart, hit.hsps));
  const stop: number = Math.max(...map((hsp: BlastHsp) => hsp.queryStop, hit.hsps));

  return start <= stop ? { start, stop } : { start: stop, stop: start };
}

export function getHitHitRange(hit: BlastHit): Range {
  const start: number = Math.min(...map((hsp: BlastHsp) => hsp.targetStart, hit.hsps));
  const stop: number = Math.max(...map((hsp: BlastHsp) => hsp.targetStop, hit.hsps));

  return start <= stop ? { start, stop } : { start: stop, stop: start };
}

export function getHitAccessionVersion(hit: BlastHit): string {
  if (!hit.target) {
    return HIT_MISSING_ACCESSION;
  }
  return hit.target.version ? `${hit.target.accession}.${hit.target.version}` : hit.target.accession;
}

export function getDatabaseAccessionVersion(hit: BlastHit): string {
  return getHitDatabaseName(hit) + ":" + getHitAccessionVersion(hit);
}

export function getHitName(hit: BlastHit): string {
  if (!hit.target) {
    return HIT_MISSING_ACCESSION;
  }
  if (hit.target.isExternal) {
    return getHitAccessionVersion(hit) + "  " + hit.target.blastIndex.name + " " + (hit.target.description || "");
  }
  return getHitAccessionVersion(hit) + " " + (hit.target.description || "");
}

export function getHspName(hit: BlastHit, hsp: BlastHsp): string {
  return `Hit ${hit.rank} HSP ${hsp.rank}`;
}

// BLAST hits table now has two separate columns (database, genome)
export function getHitDatabaseName(hit: BlastHit): string {
  if (!hit.target) {
    return null;
  }
  if (hit.target.organism && hit.target.organism.id !== 0) {
    return hit.target.organism.name;
  }
  if (hit.target.database && hit.target.database.mnemonic !== "GDP") {
    return hit.target.database.name;
  }
  if (hit.target.blastIndex) {
    // e.g. external database
    // e.g. external database
    return hit.target.blastIndex.name;
  }

  return null;
}

export interface IMaxHitRange {
  maxLeft: number;
  maxRight: number;
}

export function getMaxHitRange(proteinLength: number, listObjects: BlastResult[]): IMaxHitRange {
  let ml = 0;
  let mr = 0;
  if (listObjects) {
    listObjects.forEach((lo: BlastResult) => {
      ml = Math.max(ml, lo.targetStart - lo.queryStart);
      mr = Math.max(mr, lo.protein.length - lo.targetStop - (proteinLength - lo.queryStop));
    });
  }

  return { maxLeft: ml, maxRight: mr };
}

export function getBlastTargetLinkPath(blastHit: BlastHit, jobId = -1, queryId = -1): string {
  return !blastHit.target
    ? ""
    : blastHit.target.isExternal
      ? blastHit.target.blastIndex.url?.replace("{1}", encodeURI(blastHit.target.name))
      : `/job/${jobId !== -1 ? jobId : blastHit.jobId}/results/${
          queryId !== -1 ? queryId : blastHit.queryId
        }/hitdetails/${blastHit.rank}/summary`;
}
